import React, { FC, useEffect, useMemo, useState } from 'react'
import { Button, Dropdown, Form } from 'react-bootstrap'
import { FiSettings } from 'react-icons/fi'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatNumber, formatNumberLocale } from '../../../_utils'
import { LinkPerformanceGroupBy } from './hourly-clicks-chart'

const dashboardTableBlue = '#5b9bd5'
export const pageLineColors = [
  '#2563eb',
  '#16a34a',
  '#dc2626',
  '#9333ea',
  '#ea580c',
  '#0891b2',
  '#be123c',
  '#4f46e5',
  '#65a30d',
  '#b45309',
  '#60a5fa',
  '#4ade80',
  '#f87171',
  '#c084fc',
  '#fb923c',
  '#22d3ee',
  '#fb7185',
  '#818cf8',
  '#a3e635',
  '#facc15',
]

export type PagePerformanceMetric = 'pageViews' | 'totalClicks' | 'automatedClicks' | 'ctr' | 'commission' | 'rpm'
type PagePerformanceSelectionPreset = 'top-5' | 'top-10' | 'top-20' | 'top-30' | 'custom' | 'none'

export type PagePerformanceRow = {
  pathId: number
  domain: string
  path: string
  hour: string
  automatedClicks: number
  totalClicks: number
  pageViews: number
  commission: number
}

export type PagePerformancePage = {
  pathId: number
  domain: string
  path: string
  impressions: number
  totalClicks?: number
}

const metricLabels: Record<PagePerformanceMetric, string> = {
  pageViews: 'Page views',
  totalClicks: 'Total clicks',
  automatedClicks: 'Automated clicks',
  ctr: 'CTR',
  commission: 'Commission',
  rpm: 'RPM',
}

function parseHour(value: string) {
  const date = new Date(value)
  return Number.isFinite(date.getTime()) ? date : null
}

function parseDateValue(value: string, endOfDay = false) {
  const date = value.includes('T') ? new Date(value) : new Date(`${value}T00:00:00`)
  if (!Number.isFinite(date.getTime())) return null

  if (endOfDay) date.setHours(23, 0, 0, 0)
  else date.setHours(0, 0, 0, 0)

  return date
}

function formatHourKey(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hour = String(date.getHours()).padStart(2, '0')
  return `${year}-${month}-${day}T${hour}:00:00`
}

function formatDayKey(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}T00:00:00`
}

function getSelectedBuckets(fromDate: string, toDate: string, groupBy: LinkPerformanceGroupBy) {
  const start = parseDateValue(fromDate)
  const end = parseDateValue(toDate, true)
  if (!start || !end) return []

  const first = start <= end ? start : end
  const last = start <= end ? end : start
  const buckets: string[] = []
  const cursor = new Date(first)
  let guard = 0

  if (groupBy === 'day') {
    cursor.setHours(0, 0, 0, 0)
    last.setHours(0, 0, 0, 0)
    while (cursor <= last && guard < 20000) {
      buckets.push(formatDayKey(cursor))
      cursor.setDate(cursor.getDate() + 1)
      guard += 1
    }
    return buckets
  }

  cursor.setHours(0, 0, 0, 0)
  last.setHours(24, 0, 0, 0)
  while (cursor <= last && guard < 20000) {
    buckets.push(formatHourKey(cursor))
    cursor.setHours(cursor.getHours() + 1)
    guard += 1
  }

  return buckets
}

function getHourlyDisplayBucketKey(hour: string) {
  const date = parseHour(hour)
  if (!date) return hour
  date.setHours(date.getHours() + 1, 0, 0, 0)
  return formatHourKey(date)
}

function formatTick(value: string, groupBy: LinkPerformanceGroupBy) {
  const date = parseHour(value)
  if (!date) return value

  if (groupBy === 'hour') {
    if (date.getHours() === 0) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }

    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function getMetricValue(row: PagePerformanceRow | undefined, metric: PagePerformanceMetric) {
  if (!row) return 0
  if (metric === 'pageViews') return Number(row.pageViews) || 0
  if (metric === 'totalClicks') return Number(row.totalClicks) || 0
  if (metric === 'automatedClicks') return Number(row.automatedClicks) || 0
  if (metric === 'commission') return Number(row.commission) || 0
  if (metric === 'ctr') {
    const pageViews = Number(row.pageViews) || 0
    if (pageViews <= 0) return 0
    return ((Number(row.totalClicks) || 0) / pageViews) * 100
  }
  if (metric === 'rpm') {
    const pageViews = Number(row.pageViews) || 0
    if (pageViews <= 0) return 0
    return ((Number(row.commission) || 0) / pageViews) * 1000
  }
  return 0
}

function hasPageActivity(row: PagePerformanceRow) {
  return (
    (Number(row.pageViews) || 0) > 0 ||
    (Number(row.totalClicks) || 0) > 0 ||
    (Number(row.automatedClicks) || 0) > 0 ||
    (Number(row.commission) || 0) !== 0
  )
}

function getRowBucketKey(row: PagePerformanceRow, groupBy: LinkPerformanceGroupBy) {
  return groupBy === 'hour' ? getHourlyDisplayBucketKey(row.hour) : row.hour
}

function formatMetricValue(value: number, metric: PagePerformanceMetric, currency: string) {
  if (metric === 'commission' || metric === 'rpm') return formatNumber(value, currency)
  if (metric === 'ctr') return `${formatNumberLocale(value, 1)}%`
  return formatNumberLocale(value, 0)
}

function getPageFullLabel(page: PagePerformancePage) {
  return `${page.domain}${page.path}`
}

function getArticleLabel(page: PagePerformancePage) {
  const segments = page.path.split('/').filter(Boolean)
  if (segments.length <= 0) return getPageFullLabel(page)

  const articleSegments = segments.length > 2 ? segments.slice(1) : segments
  return articleSegments
    .map((segment) => segment.replace(/-/g, ' '))
    .join(' / ')
}

const PageSettingsToggle = React.forwardRef<HTMLButtonElement, any>(({ onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className="btn btn-sm"
    aria-label="Page chart settings"
    onClick={(event) => {
      event.preventDefault()
      onClick(event)
    }}
  >
    <FiSettings />
  </button>
))

function PageGroupBySettingsDropdown({
  selectedGroupBy,
  groupByOptions,
  onSubmit,
}: {
  selectedGroupBy: LinkPerformanceGroupBy
  groupByOptions: LinkPerformanceGroupBy[]
  onSubmit: (groupBy: LinkPerformanceGroupBy) => void
}) {
  const [show, setShow] = useState(false)
  const effectiveSelectedGroupBy = groupByOptions.includes(selectedGroupBy) ? selectedGroupBy : groupByOptions[0]
  const [internalGroupBy, setInternalGroupBy] = useState<LinkPerformanceGroupBy>(effectiveSelectedGroupBy)

  useEffect(() => {
    setInternalGroupBy(effectiveSelectedGroupBy)
  }, [effectiveSelectedGroupBy, show])

  return (
    <Dropdown align="end" show={show} onToggle={(isOpen: boolean) => setShow(isOpen)}>
      <Dropdown.Toggle as={PageSettingsToggle} id="page-performance-chart-settings" />
      <Dropdown.Menu className="p-2" style={{ minWidth: 220 }}>
        <strong className="dropdown-header">Group by</strong>
        <div className="px-2 d-flex gap-3">
          {(['hour', 'day'] as LinkPerformanceGroupBy[]).map((groupBy) => (
            <Form.Check
              key={groupBy}
              type="radio"
              id={`toggle-page-performance-group-${groupBy}`}
              name="page-performance-group-by"
              label={groupBy === 'hour' ? 'Hourly' : 'Daily'}
              checked={internalGroupBy === groupBy}
              disabled={!groupByOptions.includes(groupBy)}
              onChange={() => setInternalGroupBy(groupBy)}
              className="py-1"
            />
          ))}
        </div>
        <div className="d-flex justify-content-end mt-2 px-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setShow(false)
              onSubmit(internalGroupBy)
            }}
          >
            Submit
          </Button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )
}

const CustomTooltip: FC<any> = ({ active, payload, label, metric, currency }) => {
  const visiblePayload = (payload || []).filter((entry: any) => entry.value !== null && entry.value !== undefined)
  if (!active || !visiblePayload.length) return null

  return (
    <div className="bg-white border shadow-sm px-3 py-2 link-script-page-tooltip">
      <div className="fw-semibold mb-1">{formatTick(label, 'hour')}</div>
      {visiblePayload.map((entry: any) => (
        <div key={entry.dataKey} className="link-script-page-tooltip-row">
          <span className="link-script-page-tooltip-dot" style={{ backgroundColor: entry.color }} />
          <span className="link-script-page-tooltip-name">{entry.name}</span>
          <span className="fw-semibold ms-auto">{formatMetricValue(Number(entry.value) || 0, metric, currency)}</span>
        </div>
      ))}
    </div>
  )
}

const PagePerformanceChart: FC<{
  rows: PagePerformanceRow[]
  selectedPages: PagePerformancePage[]
  metric: PagePerformanceMetric
  currency: string
  fromDate: string
  toDate: string
  groupBy: LinkPerformanceGroupBy
  groupByOptions: LinkPerformanceGroupBy[]
  colorByPathId: Map<number, string>
  loading?: boolean
  actionsDisabled?: boolean
  selectionPreset: PagePerformanceSelectionPreset
  onMetricChange: (metric: PagePerformanceMetric) => void
  onGroupByChange: (groupBy: LinkPerformanceGroupBy) => void
  onSelectTop: (count: number) => void
  onClear: () => void
}> = ({
  rows,
  selectedPages,
  metric,
  currency,
  fromDate,
  toDate,
  groupBy,
  groupByOptions,
  colorByPathId,
  loading = false,
  actionsDisabled = false,
  selectionPreset,
  onMetricChange,
  onGroupByChange,
  onSelectTop,
  onClear,
}) => {
  const pageSummaries = useMemo(() => {
    const summaries = new Map<number, { totalClicks: number; pageViews: number }>()

    for (const row of rows) {
      const pathId = Number(row.pathId)
      const current = summaries.get(pathId) || { totalClicks: 0, pageViews: 0 }
      current.totalClicks += Number(row.totalClicks) || 0
      current.pageViews += Number(row.pageViews) || 0
      summaries.set(pathId, current)
    }

    for (const page of selectedPages) {
      if (summaries.has(page.pathId)) continue
      summaries.set(page.pathId, { totalClicks: Number(page.totalClicks) || 0, pageViews: Number(page.impressions) || 0 })
    }

    return summaries
  }, [rows, selectedPages])

  const sortedPages = useMemo(() => {
    return [...selectedPages].sort((first, second) => {
      const firstSummary = pageSummaries.get(first.pathId) || { totalClicks: 0, pageViews: Number(first.impressions) || 0 }
      const secondSummary = pageSummaries.get(second.pathId) || { totalClicks: 0, pageViews: Number(second.impressions) || 0 }
      const pageViewsDiff = secondSummary.pageViews - firstSummary.pageViews
      if (pageViewsDiff !== 0) return pageViewsDiff
      return secondSummary.totalClicks - firstSummary.totalClicks
    })
  }, [pageSummaries, selectedPages])

  const chartData = useMemo(() => {
    const buckets = getSelectedBuckets(fromDate, toDate, groupBy)
    const rowByPathAndBucket = new Map(rows.map((row) => [
      `${row.pathId}|${getRowBucketKey(row, groupBy)}`,
      row,
    ]))
    const firstActivityBucketByPathId = new Map<number, string>()

    for (const row of rows) {
      if (!hasPageActivity(row)) continue

      const pathId = Number(row.pathId)
      const bucketKey = getRowBucketKey(row, groupBy)
      const currentFirstBucket = firstActivityBucketByPathId.get(pathId)

      if (!currentFirstBucket || bucketKey < currentFirstBucket) {
        firstActivityBucketByPathId.set(pathId, bucketKey)
      }
    }

    return buckets.map((hour) => {
      const chartRow: any = { hour }
      for (const page of sortedPages) {
        const firstActivityBucket = firstActivityBucketByPathId.get(page.pathId)
        chartRow[`path-${page.pathId}`] = firstActivityBucket && hour >= firstActivityBucket
          ? getMetricValue(rowByPathAndBucket.get(`${page.pathId}|${hour}`), metric)
          : null
      }
      return chartRow
    })
  }, [fromDate, groupBy, metric, rows, sortedPages, toDate])

  return (
    <div className="mb-4 link-script-chart-panel">
      <div className="mb-2">
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2">
          <div>
            <h6 className="mb-1 fw-bold text-uppercase" style={{ color: dashboardTableBlue, fontSize: '0.95rem', letterSpacing: '0.02em' }}>
              Page performance
            </h6>
          </div>
          <div className="d-flex flex-wrap align-items-center justify-content-lg-end gap-2">
            <div className="page-performance-selection-actions">
              <div className="page-performance-selection-presets" role="group" aria-label="Page selection shortcuts">
                {[5, 10, 20, 30].map((count) => (
                  <button
                    key={count}
                    type="button"
                    className={selectionPreset === `top-${count}` ? 'is-active' : ''}
                    aria-pressed={selectionPreset === `top-${count}`}
                    disabled={actionsDisabled}
                    onClick={() => onSelectTop(count)}
                  >
                    Top {count}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="page-performance-clear-button"
                disabled={actionsDisabled || selectedPages.length === 0}
                onClick={onClear}
              >
                Clear all
              </button>
            </div>
            <div className="d-flex align-items-center gap-1">
              <Form.Select
                size="sm"
                value={metric}
                onChange={(event) => onMetricChange(event.target.value as PagePerformanceMetric)}
                style={{ width: 180 }}
              >
                {(Object.keys(metricLabels) as PagePerformanceMetric[]).map((key) => (
                  <option key={key} value={key}>{metricLabels[key]}</option>
                ))}
              </Form.Select>
              <PageGroupBySettingsDropdown
                selectedGroupBy={groupBy}
                groupByOptions={groupByOptions}
                onSubmit={onGroupByChange}
              />
            </div>
          </div>
        </div>
        <div className="mt-2" style={{ height: 1, backgroundColor: 'rgba(91, 155, 213, 0.35)' }} />
      </div>

      {selectedPages.length === 0 ? (
        <div className="link-script-empty-chart">
          Select pages from Page click report
        </div>
      ) : (
        <>
          <div className="position-relative" style={{ width: '100%', height: 380 }}>
            {loading && (
              <div className="link-script-chart-loading">
                <span className="spinner-border spinner-border-sm" />
              </div>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,.08)" />
                <XAxis
                  dataKey="hour"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => formatTick(String(value), groupBy)}
                  minTickGap={24}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => formatMetricValue(Number(value) || 0, metric, currency)}
                  width={metric === 'commission' || metric === 'rpm' ? 68 : 48}
                />
                <Tooltip content={<CustomTooltip metric={metric} currency={currency} />} />
                {sortedPages.map((page) => (
                  <Line
                    key={page.pathId}
                    type="monotone"
                    dataKey={`path-${page.pathId}`}
                    name={getArticleLabel(page)}
                    stroke={colorByPathId.get(page.pathId) || dashboardTableBlue}
                    strokeWidth={2.4}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  )
}

export default React.memo(PagePerformanceChart)
